import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LanguagesForm from "../../components/languageslist/languageslist";
import Modal from "../../components/modal/modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faXmark,
  faGlobe,
  faPhone,
  faEnvelope,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";

// 🔹 Иконки для социальных сетей
import {
  faInstagram,
  faFacebook,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";

// 🔹 Компонент для копирования ID пользователя
const UserIdCopy = ({ userId }) => {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(userId.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
      const textArea = document.createElement("textarea");
      textArea.value = userId.toString();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-500 text-lg font-medium">ID: {userId}</span>
      <button onClick={handleCopy}>
        {copied ? (
          <>
            <span className="text-[#006674]"> {t("copied")}</span>
          </>
        ) : (
          <>
            <FontAwesomeIcon className="text-xl text-gray-500" icon={faCopy} />
          </>
        )}
      </button>
    </div>
  );
};

// 🔹 Компонент для добавления нескольких телефонов
const PhoneNumbersField = ({ phones, setPhones, isEditing, error }) => {
  const { t } = useTranslation();

  const addPhone = () => {
    setPhones([...phones, ""]);
  };

  const removePhone = (index) => {
    const newPhones = phones.filter((_, i) => i !== index);
    setPhones(newPhones);
  };

  const updatePhone = (index, value) => {
    const newPhones = [...phones];
    newPhones[index] = value;
    setPhones(newPhones);
  };

  const validPhones = phones.filter((phone) => phone?.trim());

  // Не отображаем блок, если нет телефонов и не в режиме редактирования
  if (!isEditing && validPhones.length === 0) return null;

  return (
    <div className="w-full mb-6">
      {isEditing ? (
        <div className="space-y-3">
          {phones.map((phone, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="tel"
                value={phone}
                onChange={(e) => updatePhone(index, e.target.value)}
                placeholder={t("enterphone")}
                className={`w-[400px] max-sm:w-full border px-3 py-2 rounded-md outline-none focus:border-[#006674] ${
                  error && !phone?.trim() ? "border-red-500" : ""
                }`}
              />
              {phones.length > 1 && (
                <button
                  type="button"
                  onClick={() => removePhone(index)}
                  className="bg-red-500 text-white w-[40px] h-[40px] rounded-full flex justify-center items-center hover:bg-red-600 flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addPhone}
            className="flex gap-2 bg-[#006674] text-white w-[40px] h-[40px] rounded-full justify-center items-center hover:bg-[#004c55]"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
          {error && phones.every((phone) => !phone?.trim()) && (
            <p className="text-red-500 text-sm">{t("fieldRequired")}</p>
          )}
        </div>
      ) : validPhones.length > 0 ? (
        <div className="space-y-1">
          {validPhones.map((phone, index) => (
            <p
              key={index}
              className="text-lg flex justify-start items-center gap-2 text-[#006674]"
            >
              <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faPhone} />
              </div>
              {phone}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// 🔹 Компонент для Email (отдельно от других ссылок)
const EmailField = ({ email, setEmail, isEditing, error }) => {
  const { t } = useTranslation();

  // Не отображаем блок, если нет email и не в режиме редактирования
  if (!isEditing && !email?.trim()) return null;

  return (
    <div className="w-full mb-6">
      {isEditing ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 max-sm:flex-col max-sm:items-start">
            <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center flex-shrink-0">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <div className="flex justify-start items-center gap-3 w-full">
              <span className="text-red-500 text-xl">*</span>
              <input
                type="email"
                value={email || ""}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("enteremaillink")}
                className={`w-[400px] max-sm:w-full border px-3 py-2 rounded-md outline-none focus:border-[#006674] ${
                  error ? "border-red-500" : ""
                }`}
              />
            </div>
          </div>
        </div>
      ) : email?.trim() ? (
        <div className="flex items-center gap-3">
          <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center">
            <FontAwesomeIcon icon={faEnvelope} />
          </div>
          <a
            href={`mailto:${email}`}
            className="text-xl text-[#006674] hover:underline"
          >
            {email}
          </a>
        </div>
      ) : null}
    </div>
  );
};

// 🔹 Компонент для добавления нескольких ссылок (без email)
const LinksField = ({ links, setLinks, isEditing }) => {
  const { t } = useTranslation();

  const addLink = () => {
    setLinks([...links, ""]);
  };

  const removeLink = (index) => {
    const newLinks = links.filter((_, i) => i !== index);
    setLinks(newLinks);
  };

  const updateLink = (index, value) => {
    const newLinks = [...links];
    newLinks[index] = value;
    setLinks(newLinks);
  };

  const validLinks = links.filter((link) => link?.trim());

  // Не отображаем блок, если нет ссылок и не в режиме редактирования
  if (!isEditing && validLinks.length === 0) return null;

  return (
    <div className="w-full mb-2">
      {isEditing ? (
        <div className="space-y-3">
          {links.map((link, index) => (
            <div key={index} className="flex w-full max-w-[400px] items-center gap-2">
              <input
                type="url"
                value={link}
                onChange={(e) => updateLink(index, e.target.value)}
                placeholder={t("enterlink")}
                className="w-full border px-3 py-2 rounded-md outline-none focus:border-[#006674]"
              />
              {links.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLink(index)}
                  className="bg-red-500 text-white w-[40px] h-[40px] flex justify-center items-center rounded-full hover:bg-red-600 flex-shrink-0"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addLink}
            className="flex items-center justify-center gap-2 bg-[#006674] text-white w-[40px] h-[40px] rounded-full hover:bg-[#004c55]"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      ) : validLinks.length > 0 ? (
        <div className="space-y-1">
          {validLinks.map((link, index) => (
            <a
              key={index}
              href={link.startsWith("http") ? link : `https://${link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-start items-center gap-2 text-xl text-blue-600 hover:underline"
            >
              <div className="bg-blue-500 text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faGlobe} />
              </div>
              {link}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
};

// 🔹 Компонент для социальных сетей (без email)
const SocialMediaField = ({ socialMedia, setSocialMedia, isEditing }) => {
  const { t } = useTranslation();

  const socialPlatforms = [
    {
      key: "facebook",
      icon: faFacebook,
      placeholder: t("enterfacebooklink"),
      color: "bg-blue-600",
    },
    {
      key: "instagram",
      icon: faInstagram,
      placeholder: t("enterinstagramlink"),
      color: "bg-pink-500",
    },
    {
      key: "linkedin",
      icon: faLinkedin,
      placeholder: t("enterlinkedin"),
      color: "bg-blue-700",
    },
    {
      key: "youtube",
      icon: faYoutube,
      placeholder: t("enteryoutubelink"),
      color: "bg-red-600",
    },
  ];

  const updateSocialMedia = (platform, value) => {
    setSocialMedia((prev) => ({
      ...prev,
      [platform]: value,
    }));
  };

  const getSocialMediaValue = (platformKey) => {
    return socialMedia && socialMedia[platformKey]
      ? socialMedia[platformKey]
      : "";
  };

  const hasSocialMediaContent = socialPlatforms.some((platform) => {
    const value = getSocialMediaValue(platform.key);
    return value && typeof value === "string" && value.trim();
  });

  // Не отображаем блок, если нет соцсетей и не в режиме редактирования
  if (!isEditing && !hasSocialMediaContent) return null;

  return (
    <div className="w-full mb-10">
      {isEditing ? (
        <div className="space-y-4">
          {socialPlatforms.map((platform) => (
            <div key={platform.key} className="flex items-center gap-3">
              <div
                className={`${platform.color} text-white w-10 h-10 text-2xl rounded-full flex items-center justify-center flex-shrink-0`}
              >
                <FontAwesomeIcon icon={platform.icon} />
              </div>
              <input
                type="url"
                value={getSocialMediaValue(platform.key)}
                onChange={(e) =>
                  updateSocialMedia(platform.key, e.target.value)
                }
                placeholder={platform.placeholder}
                className="w-[400px] max-sm:w-full border px-3 py-2 rounded-md outline-none focus:border-[#006674]"
              />
            </div>
          ))}
        </div>
      ) : hasSocialMediaContent ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-6">
          {socialPlatforms.map((platform) => {
            const value = getSocialMediaValue(platform.key);
            return value && typeof value === "string" && value.trim() ? (
              <a
                key={platform.key}
                href={value.startsWith("http") ? value : `https://${value}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-xl"
              >
                <div
                  className={`${platform.color} text-white w-10 h-10 rounded-full flex items-center justify-center`}
                >
                  <FontAwesomeIcon icon={platform.icon} />
                </div>
                <span className="text-[#006674] hover:underline">
                  {platform.key.charAt(0).toUpperCase() + platform.key.slice(1)}
                </span>
              </a>
            ) : null;
          })}
        </div>
      ) : null}
    </div>
  );
};

// 🔹 Поле для названия работы (появляется при выборе "Ищу работу")
const JobTitleField = ({ jobTitle, setJobTitle, isEditing, show, error }) => {
  const { t } = useTranslation();

  if (!show) return null;

  // Не отображаем блок, если нет данных и не в режиме редактирования
  if (!isEditing && !jobTitle?.trim()) return null;

  return (
    <div className="w-full mt-12 mb-10">
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {t("Position")} {isEditing && <span className="text-red-500">*</span>}
      </h2>

      {isEditing ? (
        <div>
          <input
            type="text"
            value={jobTitle || ""}
            onChange={(e) => setJobTitle(e.target.value)}
            placeholder={t("enterhatsiq")}
            className={`w-full border px-3 py-2 rounded-md outline-none transition-all duration-200 focus:border-[#006674] ${
              error ? "border-red-500" : ""
            }`}
          />
          {error && (
            <p className="text-red-500 text-sm mt-1">{t("fieldRequired")}</p>
          )}
        </div>
      ) : jobTitle ? (
        <p className="text-lg">{jobTitle}</p>
      ) : null}
    </div>
  );
};

// 🔹 Поле для названия услуги (появляется при выборе "Предлагаю услуги")
const ServiceNameField = ({
  serviceName,
  setServiceName,
  isEditing,
  show,
  error,
}) => {
  const { t } = useTranslation();

  if (!show) return null;

  // Не отображаем блок, если нет данных и не в режиме редактирования
  if (!isEditing && !serviceName?.trim()) return null;

  return (
    <div className="w-full mt-12 mb-10">
      <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
        {t("service")} {isEditing && <span className="text-red-500">*</span>}
      </h2>

      {isEditing ? (
        <div className="flex justify-start items-center gap-2">
          <input
            type="text"
            value={serviceName || ""}
            onChange={(e) => setServiceName(e.target.value)}
            placeholder={t("enterservices")}
            className={`w-full border px-3 py-2 rounded-md outline-none transition-all duration-200 focus:border-[#006674] ${
              error ? "border-red-500" : ""
            }`}
          />
        </div>
      ) : serviceName ? (
        <p className="text-lg">{serviceName}</p>
      ) : null}
    </div>
  );
};

// 🔹 Дропдаун для выбора сфер
const SphereDropdown = ({
  value,
  onChange,
  placeholder,
  options,
  isEditing,
  error,
}) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  if (!isEditing) {
    const selectedOption = options.find((opt) => opt.key === value);
    if (!selectedOption) return null;
    return <p className="text-lg">{selectedOption.label}</p>;
  }

  return (
    <div className="relative flex justify-start items-center gap-2 w-[400px] max-sm:w-full">
      <input
        type="text"
        readOnly
        placeholder={placeholder}
        className={`w-full py-2 px-4 border rounded-md outline-none cursor-pointer focus:border-[#006674] ${
          error ? "border-red-500" : ""
        }`}
        value={options.find((opt) => opt.key === value)?.label || ""}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute bg-white border top-full rounded-md p-2 mt-1 z-50 shadow w-full max-h-60 overflow-y-auto">
          {options.map((opt) => (
            <div
              key={opt.key}
              className="p-2 hover:bg-[#006674] hover:text-white cursor-pointer rounded"
              onClick={() => {
                onChange(opt.key);
                setShow(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p className="text-red-500 text-sm mt-1">{t("fieldRequired")}</p>
      )}
    </div>
  );
};

// 🔹 Компонент для отображения сферы (только просмотр)
const SphereField = ({ value, options }) => {
  if (!value) return null;

  const selectedOption = options.find((opt) => opt.key === value);
  if (!selectedOption) return null;

  return <p className="text-lg">{selectedOption.label}</p>;
};

// 🔹 Группа радио-кнопок для выбора типа услуги
const ServiceTypeField = ({
  serviceType,
  setServiceType,
  isEditing,
  error,
}) => {
  const { t } = useTranslation();

  const options = [
    { key: "offer_services", label: t("offerservices") },
    { key: "search_work", label: t("searchworktitle") },
  ];

  const handleChange = (selectedKey) => {
    if (isEditing) {
      setServiceType(selectedKey);
    }
  };

  // Не отображаем блок, если нет типа услуги и не в режиме редактирования
  if (!isEditing && !serviceType) return null;

  return (
    <div className="w-full mb-3">
      {isEditing ? (
        <div className="space-y-3">
          {options.map((option) => (
            <label
              key={option.key}
              className="flex items-center bg-[#006674] w-[300px] max-sm:w-full rounded-lg py-2 px-3 space-x-3 cursor-pointer"
            >
              <input
                type="radio"
                name="serviceType"
                value={option.key}
                checked={serviceType === option.key}
                onChange={() => handleChange(option.key)}
                className="w-5 h-5 text-[#006674] border-gray-300 focus:ring-[#006674]"
              />
              <span className="text-white">{option.label}</span>
            </label>
          ))}
        </div>
      ) : serviceType ? (
        <p className="py-2 text-[#006674] text-3xl max-sm:text-2xl font-bold uppercase">
          {options.find((opt) => opt.key === serviceType)?.label}
        </p>
      ) : null}
    </div>
  );
};

// 🔹 Переключатель видимости
const VisibilityToggle = ({ visibility, setVisibility }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center bg-white p-3 rounded-md justify-between w-[400px] max-sm:w-full mb-4">
      <h2 className="text-[#006674] text-xl font-bold">{t("visibility")}</h2>
      <button
        onClick={() => setVisibility(!visibility)}
        className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none ${
          visibility ? "bg-[#006674]" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
            visibility ? "translate-x-7" : "translate-x-0"
          }`}
        ></span>
      </button>
    </div>
  );
};

// 🔹 Поле ввода даты
const DateInput = ({ placeholder, value, onChange, width, error }) => (
  <input
    type="number"
    placeholder={placeholder}
    className={`border px-3 py-2 rounded-md outline-none transition-all duration-200 text-center ${width} focus:border-[#006674] focus:shadow-md ${
      error ? "border-red-500" : ""
    }`}
    value={value}
    onChange={onChange}
  />
);

// 🔹 Выбор пола
const GenderPicker = ({ value, onChange, placeholder, error }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  const options = [
    { key: "male", label: t("male") },
    { key: "female", label: t("female") },
    { key: "other", label: t("other") },
  ];

  const selectedLabel = options.find((opt) => opt.key === value)?.label || "";

  return (
    <div className="relative w-[150px] max-sm:w-full">
      <div className="flex justify-start items-center gap-2">
        <span className="text-red-500">*</span>
        <input
          type="text"
          readOnly
          placeholder={placeholder}
          className={`w-full py-2 px-4 border rounded-md outline-none cursor-pointer focus:border-[#006674] ${
            error ? "border-red-500" : ""
          }`}
          value={selectedLabel}
          onClick={() => setShow(!show)}
        />
      </div>
      {show && (
        <div className="absolute bg-white border rounded-md p-2 mt-1 z-50 shadow w-full">
          {options.map((opt) => (
            <div
              key={opt.key}
              className="p-2 hover:bg-[#006674] hover:text-white cursor-pointer rounded"
              onClick={() => {
                onChange(opt.key);
                setShow(false);
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p className="text-red-500 text-sm mt-1">{t("fieldRequired")}</p>
      )}
    </div>
  );
};

const emptyLanguageRow = () => ({ name: "", level: "", level2: "" });

// 🔹 Вспомогательная функция для парсинга языков
const parseLanguages = (languagesData) => {
  const defaultRows = [emptyLanguageRow()];
  if (!languagesData) return defaultRows;
  if (Array.isArray(languagesData)) {
    return languagesData.length > 0 ? languagesData : defaultRows;
  }
  if (typeof languagesData === "string") {
    try {
      const parsed = JSON.parse(languagesData);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      return defaultRows;
    } catch (e) {
      return defaultRows;
    }
  }
  return defaultRows;
};

// 🔹 Основная страница
const ViewCandidatePage = () => {
  const { t } = useTranslation();

  const [modal, setModal] = useState({
    show: false,
    title: t("error") || "Error",
    text: t("markererror") || "Something went wrong",
    success: false,
    ok: t("ok") || "OK",
  });
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [visibility, setVisibility] = useState(true);

  // 🔹 Базовые данные
  const [name, setName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState("");

  // 🔹 Доп. информация
  const [jobTitle, setJobTitle] = useState("");
  const [jobPeriod, setJobPeriod] = useState("");
  const [jobTitles, setJobTitles] = useState("");
  const [education, setEducation] = useState("");
  const [extraEducation, setExtraEducation] = useState("");
  const [awards, setAwards] = useState("");
  const [volunteer, setVolunteer] = useState("");
  const [languages, setLanguages] = useState([emptyLanguageRow()]);
  const [skills, setSkills] = useState("");
  const [computerKnowledge, setComputerKnowledge] = useState("");
  const [extraInfo, setExtraInfo] = useState("");

  // 🔹 НОВЫЕ ПОЛЯ: услуга и сферы
  const [serviceType, setServiceType] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [mainSphere, setMainSphere] = useState("");
  const [additionalSphere, setAdditionalSphere] = useState("");

  // 🔹 НОВЫЕ ПОЛЯ: контакты
  const [phones, setPhones] = useState([""]);
  const [email, setEmail] = useState("");
  const [links, setLinks] = useState([""]);

  // 🔹 НОВЫЕ ПОЛЯ: социальные сети
  const [socialMedia, setSocialMedia] = useState({
    instagram: "",
    facebook: "",
    linkedin: "",
    youtube: "",
  });

  // 🔹 Состояние для ошибок валидации
  const [errors, setErrors] = useState({});

  // 🔹 Опции для сфер деятельности из t("filtres")
  const sphereOptions = t("filtres", { returnObjects: true }).map(
    (item, index) => ({
      key: `sphere_${index}`,
      label: item,
    }),
  );

  // 🔹 Загружаем пользователя
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userId = localStorage.getItem("userId");
        if (!userId) {
          setIsLoading(false);
          return;
        }
        const response = await fetch(
          `/api/get_user.php?id=${userId}`,
        );
        const data = await response.json();
        if (data.success) {
          setUser(data.user);

          // Основные поля
          setName(data.user.name || "");
          setLastName(data.user.lastName || "");
          setGender(data.user.gender || "");

          // Дата рождения
          if (data.user.birthday) {
            const parts = data.user.birthday.split("-");
            setBirthYear(parts[0] || "");
            setBirthMonth(parts[1] || "");
            setBirthDay(parts[2] || "");
          }

          // Поля из extra_info
          setJobTitle(data.user.jobTitle || "");
          setJobPeriod(data.user.jobPeriod || "");
          setJobTitles(data.user.jobTitles || "");
          setEducation(data.user.education || "");
          setExtraEducation(data.user.extraEducation || "");
          setAwards(data.user.awards || "");
          setVolunteer(data.user.volunteer || "");

          setLanguages(parseLanguages(data.user.languages));
          setSkills(data.user.skills || "");
          setComputerKnowledge(data.user.computerKnowledge || "");
          setExtraInfo(data.user.extraInfo || "");
          setVisibility(data.user.visibility || false);

          // 🔹 Новые поля
          setServiceType(data.user.serviceType || "");
          setServiceName(data.user.serviceName || "");
          setMainSphere(data.user.mainSphere || "");
          setAdditionalSphere(data.user.additionalSphere || "");

          // 🔹 Загружаем контакты
          if (data.phones && data.phones.length > 0) {
            setPhones(data.phones.map((p) => p.phone || ""));
          }
          if (data.websites && data.websites.length > 0) {
            setLinks(data.websites.map((w) => w.url || ""));
          }

          // 🔹 Загружаем социальные сети и email отдельно
          if (data.social_media) {
            setEmail(data.social_media.contact_email || "");
            setSocialMedia({
              instagram: data.social_media.instagram || "",
              facebook: data.social_media.facebook || "",
              linkedin: data.social_media.linkedin || "",
              youtube: data.social_media.youtube || "",
            });
          } else {
            setEmail("");
            setSocialMedia({
              instagram: "",
              facebook: "",
              linkedin: "",
              youtube: "",
            });
          }
          if (!data.user.name || !data.user.lastName || !data.user.gender) {
            setIsEditing(true);
          }
        }
      } catch (err) {
        console.error("Ошибка загрузки профиля:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
  }, []);

  // 🔹 Валидация формы
  const validateForm = () => {
    const newErrors = {};

    // Обязательные поля
    if (!name?.trim()) newErrors.name = true;
    if (!lastName?.trim()) newErrors.lastName = true;
    if (!birthDay || !birthMonth || !birthYear) newErrors.birthday = true;
    if (!gender) newErrors.gender = true;
    if (!serviceType) newErrors.serviceType = true;

    // В зависимости от типа услуги
    if (serviceType === "search_work" && !jobTitles?.trim()) {
      newErrors.jobTitles = true;
    }
    if (serviceType === "offer_services" && !serviceName?.trim()) {
      newErrors.serviceName = true;
    }

    // Хотя бы одна сфера
    if (!mainSphere) newErrors.mainSphere = true;

    // Языки - хотя бы один язык с названием
    const hasValidLanguage = languages.some(
      (lang) => lang.name && lang.name?.trim(),
    );
    if (!hasValidLanguage) newErrors.languages = true;

    // Навыки
    if (!skills?.trim()) newErrors.skills = true;

    // Телефоны - хотя бы один телефон
    const hasValidPhone = phones.some((phone) => phone?.trim());
    if (!hasValidPhone) newErrors.phones = true;

    if (!email || typeof email !== "string" || !email.trim()) {
      newErrors.email = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔹 Сохраняем
 // 🔹 Сохраняем
// 🔹 Сохраняем
// 🔹 Сохраняем
const handleSaveAll = async () => {
  if (!validateForm()) {
    setModal({
      show: true,
      title: t("error"),
      text: t("markererror"),
      success: false,
      ok: t("ok"),
    });
    return;
  }

  const userId = localStorage.getItem("userId");
  console.log("🔍 userId:", userId);

  if (!userId || userId === "null" || userId === "undefined") {
    setModal({
      show: true,
      title: "Ошибка",
      text: "Пользователь не авторизован",
      success: false,
      ok: "OK",
    });
    return;
  }

  const birthday =
    birthYear && birthMonth && birthDay
      ? `${birthYear}-${birthMonth}-${birthDay}`
      : "";

  const formData = new FormData();
  formData.append("id", userId);
  formData.append("name", name);
  formData.append("lastName", lastName);
  formData.append("gender", gender);
  formData.append("birthday", birthday);

  // Существующие поля
  formData.append("jobTitle", jobTitle);
  formData.append("jobPeriod", jobPeriod);
  formData.append("jobTitles", jobTitles);
  formData.append("education", education);
  formData.append("extraEducation", extraEducation);
  formData.append("awards", awards);
  formData.append("volunteer", volunteer);

  formData.append("languages", JSON.stringify(languages));
  formData.append("skills", skills);
  formData.append("computerKnowledge", computerKnowledge);
  formData.append("extraInfo", extraInfo);
  formData.append("visibility", visibility ? "1" : "0");

  // Новые поля
  formData.append("serviceType", serviceType);
  formData.append("serviceName", serviceName);
  formData.append("mainSphere", mainSphere);
  formData.append("additionalSphere", additionalSphere);

  // Социальные сети и email
  formData.append("contact_email", email || "");
  formData.append("instagram", socialMedia.instagram || "");
  formData.append("facebook", socialMedia.facebook || "");
  formData.append("linkedin", socialMedia.linkedin || "");
  formData.append("youtube", socialMedia.youtube || "");

  // Телефоны и ссылки
  const validPhones = phones.filter((phone) => phone?.trim());
  validPhones.forEach((phone) => {
    formData.append("phones[]", phone);
  });

  const validLinks = links.filter((link) => link?.trim());
  validLinks.forEach((link) => {
    formData.append("websites[]", link);
  });

  try {
    const response = await fetch(`/api/update_user.php?id=${userId}`, {
      method: "POST",
      body: formData,
    });
    
    console.log("Response status:", response.status);
    
    // Проверяем, что ответ не пустой
    const text = await response.text();
    console.log("Response text:", text);
    
    if (!text) {
      throw new Error("Пустой ответ от сервера");
    }
    
    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      throw new Error("Невалидный JSON: " + text.substring(0, 100));
    }
    
    if (data.success) {
      setUser((prev) => ({
        ...prev,
        name,
        lastName,
        gender,
        birthday,
        jobTitle,
        jobTitles,
        jobPeriod,
        education,
        extraEducation,
        awards,
        volunteer,
        languages,
        skills,
        computerKnowledge,
        extraInfo,
        visibility,
        serviceType,
        serviceName,
        mainSphere,
        additionalSphere,
      }));
      setIsEditing(false);
      setErrors({});
      setModal({
        show: true,
        title: t("success"),
        text: t("savesuccess"),
        success: true,
        ok: t("ok"),
      });
    } else {
      setModal({
        show: true,
        title: t("error"),
        text: data.message || t("errortitle"),
        success: false,
        ok: t("ok"),
      });
    }
  } catch (err) {
    console.error("Ошибка:", err);
    setModal({
      show: true,
      title: "Ошибка соединения",
      text: err.message || "Проверьте подключение к интернету",
      success: false,
      ok: t("ok"),
    });
  }
};

  if (isLoading)
    return <p className="text-center text-white mt-10">{t("loading")}...</p>;
  if (!user)
    return <p className="text-center text-white mt-10">{t("error")}</p>;

  // 🔹 Вспомогательная функция для отображения полей
  const renderField = (
    stateValue,
    setState,
    placeholder,
    isTextArea = false,
    fieldError = false,
    showAsterisk = false,
  ) => {
    // Не отображаем блок, если нет данных и не в режиме редактирования
    if (!isEditing && !stateValue?.trim()) return null;

    return isEditing ? (
      isTextArea ? (
        <div className="w-[400px] max-sm:w-full mb-10">
          <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
            {placeholder}
          </h2>
          <div className="flex justify-start items-center gap-2">
            {showAsterisk && (
              <span className="text-red-500 text-lg z-10">*</span>
            )}
            <textarea
              className={`w-full border rounded-md p-2 outline-none transition-all duration-200 focus:border-[#006674] focus:shadow-md ${
                fieldError ? "border-red-500" : ""
              }`}
              placeholder={placeholder}
              value={stateValue || ""}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="w-[400px] max-sm:w-full mb-10">
          <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
            {placeholder}
          </h2>
          <div>
            <input
              type="text"
              className={`w-full border rounded-md p-2 outline-none transition-all duration-200 focus:border-[#006674] focus:shadow-md ${
                fieldError ? "border-red-500" : ""
              }`}
              placeholder={placeholder}
              value={stateValue || ""}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
        </div>
      )
    ) : (
      <div className="w-[400px] max-sm:w-full mb-10">
        <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
          {placeholder}
        </h2>
        <p className="text-lg">{stateValue}</p>
      </div>
    );
  };

  const renderField2 = (
    stateValue,
    setState,
    placeholder,
    isTextArea = false,
    fieldError = false,
  ) => {
    // Не отображаем блок, если нет данных и не в режиме редактирования
    if (!isEditing && !stateValue?.trim()) return null;

    return isEditing ? (
      isTextArea ? (
        <div className="w-full">
          <textarea
            className={`w-full border rounded-md p-2 outline-none transition-all duration-200 focus:border-[#006674] focus:shadow-md ${
              fieldError ? "border-red-500" : ""
            }`}
            placeholder={placeholder}
            value={stateValue || ""}
            onChange={(e) => setState(e.target.value)}
          />
        </div>
      ) : (
        <div className="w-full">
          <div className="flex justify-start items-center gap-2">
            <span className="text-red-500">*</span>
            <input
              type="text"
              className={`w-full border rounded-md p-2 outline-none transition-all duration-200 focus:border-[#006674] focus:shadow-md ${
                fieldError ? "border-red-500" : ""
              }`}
              placeholder={placeholder}
              value={stateValue || ""}
              onChange={(e) => setState(e.target.value)}
            />
          </div>
        </div>
      )
    ) : (
      <p className="text-2xl text-[#006674] font-semibold">{stateValue}</p>
    );
  };

  return (
   <section className="min-h-screen">
  {modal.show && (
    <Modal
      title={modal.title}
      text={modal.text}
      success={modal.success}
      onClose={() => setModal((prev) => ({ ...prev, show: false }))}
      actions={[
        {
          label: modal.ok || t("ok"),
          onClick: () => {
            setModal((prev) => ({ ...prev, show: false }));
          },
          success: !modal.success,
        },
      ]}
    />
  )}
  <div className="w-[1000px] max-xl:w-full m-auto bg-white shadow-2xl p-3 shadow-black border-[2px] border-[#006674] flex flex-col items-start">
    <div className="flex w-full justify-start gap-3 items-center flex-wrap">
      <div className="w-44 h-44 translate-y-1 overflow-hidden rounded-2xl border-4 border-white shadow-lg mb-8 max-sm:w-32 max-sm:h-32 max-sm:mb-4">
        <img
          src={
            user.photo
              ? `/api/uploads/${user.photo}`
              : "/default-avatar.png"
          }
          alt="User Avatar"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex justify-start items-start flex-col gap-3">
          {/* Имя и фамилия */}
          <div className="flex justify-start items-center gap-12 max-sm:gap-4 max-sm:flex-col max-sm:items-start max-sm:w-full">
            <div className="max-sm:w-full">
              {(isEditing || name?.trim()) && (
                <div className="w-max max-sm:w-full flex justify-start items-center gap-2 mb-2">
                  {renderField2(
                    name,
                    setName,
                    t("name"),
                    false,
                    errors.name,
                  )}
                </div>
              )}
              {(isEditing || lastName?.trim()) && (
                <div className="w-max max-sm:w-full flex justify-start items-center gap-2">
                  {renderField2(
                    lastName,
                    setLastName,
                    t("lastname"),
                    false,
                    errors.lastName,
                  )}
                </div>
              )}
            </div>

            <div className="max-sm:w-full">
              {/* Дата рождения */}
              {(isEditing || (birthDay && birthMonth && birthYear)) && (
                <div className="w-[300px] max-sm:w-full mb-2">
                  {isEditing ? (
                    <div>
                      <div className="flex justify-start items-center gap-2 max-sm:flex-wrap">
                        <span className="text-red-500">*</span>
                        <DateInput
                          placeholder={t("day")}
                          width="w-20 max-sm:w-full max-sm:flex-1"
                          value={birthDay}
                          onChange={(e) => setBirthDay(e.target.value)}
                          error={errors.birthday}
                        />
                        <DateInput
                          placeholder={t("month")}
                          width="w-20 max-sm:w-full max-sm:flex-1"
                          value={birthMonth}
                          onChange={(e) => setBirthMonth(e.target.value)}
                          error={errors.birthday}
                        />
                        <DateInput
                          placeholder={t("year")}
                          width="w-24 max-sm:w-full max-sm:flex-1"
                          value={birthYear}
                          onChange={(e) => setBirthYear(e.target.value)}
                          error={errors.birthday}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-lg mt-1">{`${birthDay}-${birthMonth}-${birthYear}`}</p>
                  )}
                </div>
              )}

              {/* Пол */}
              {(isEditing || gender) && (
                <div className="w-[300px] max-sm:w-full">
                  {isEditing ? (
                    <GenderPicker
                      value={gender}
                      onChange={setGender}
                      placeholder={t("gender")}
                      error={errors.gender}
                    />
                  ) : gender ? (
                    <p className="text-lg mt-3">
                      {
                        {
                          male: t("male"),
                          female: t("female"),
                          other: t("other"),
                        }[gender]
                      }
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          {/* 🔹 КОМПОНЕНТ КОПИРОВАНИЯ ID */}
          <UserIdCopy userId={user.id} />
        </div>
      </div>
    </div>

    <div className="p-3 w-full">
      {/* 🔹 Тип услуги */}
      <ServiceTypeField
        serviceType={serviceType}
        setServiceType={setServiceType}
        isEditing={isEditing}
        error={errors.serviceType}
      />

      {/* Заголовок "Это предложение работы" показываем только если есть тип услуги */}
      {(serviceType || isEditing) && (
        <h2 className="text-3xl max-sm:text-2xl font-bold text-[#006674] uppercase mb-8">
          {t("thisjobstitle")}
        </h2>
      )}

      {/* 🔹 Поле для названия работы */}
      <JobTitleField
        jobTitle={jobTitles}
        setJobTitle={setJobTitles}
        isEditing={isEditing}
        show={serviceType === "search_work"}
        error={errors.jobTitles}
      />

      {/* 🔹 Поле для названия услуги */}
      <ServiceNameField
        serviceName={serviceName}
        setServiceName={setServiceName}
        isEditing={isEditing}
        show={serviceType === "offer_services"}
        error={errors.serviceName}
      />

      {/* 🔹 Сферы деятельности */}
      {(mainSphere || additionalSphere || isEditing) && (
        <>
          <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
            {t("industry")}
          </h2>
          <div className="w-auto flex justify-start items-center gap-3 mb-10 max-sm:flex-col max-sm:items-start">
            {/* 🔹 Основная сфера деятельности */}
            {(isEditing || mainSphere) && (
              <div className="w-auto max-md:w-full flex justify-start items-center gap-2">
                {isEditing ? <span className="text-red-500">*</span> : ""}
                {isEditing ? (
                  <SphereDropdown
                    value={mainSphere}
                    onChange={setMainSphere}
                    placeholder={t("sentfirtsspere")}
                    options={sphereOptions}
                    isEditing={isEditing}
                    error={errors.mainSphere}
                  />
                ) : (
                  <SphereField value={mainSphere} options={sphereOptions} />
                )}
              </div>
            )}

            {/* 🔹 Дополнительная сфера деятельности */}
            {(isEditing || additionalSphere) && (
              <div className="w-auto max-md:w-full">
                {isEditing ? (
                  <SphereDropdown
                    value={additionalSphere}
                    onChange={setAdditionalSphere}
                    placeholder={t("sentsecondsspere")}
                    options={sphereOptions}
                    isEditing={isEditing}
                  />
                ) : (
                  <SphereField
                    value={additionalSphere}
                    options={sphereOptions}
                  />
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Опыт работы */}
      {renderField(jobTitle, setJobTitle, t("workexperiance"))}
      {renderField(jobPeriod, setJobPeriod, t("periudwork"))}

      {/* Образование и остальные поля */}
      {renderField(education, setEducation, t("Education"), true)}
      {renderField(
        extraEducation,
        setExtraEducation,
        t("extraeducation"),
        true,
      )}
      {renderField(awards, setAwards, t("ACHIEVEMENTS AND AWARDS"), true)}
      {renderField(volunteer, setVolunteer, t("Volunteer"), true)}

      {/* Языки */}
      {(languages.some((lang) => lang.name?.trim()) || isEditing) && (
        <div className="w-[500px] max-sm:w-full mb-10">
          <h2 className="text-xl font-bold text-[#006674] uppercase mb-2">
            {t("languages")}
          </h2>
          <LanguagesForm
            formData={{
              languages: parseLanguages(languages),
            }}
            setFormData={({ languages }) => setLanguages(languages)}
            isEditing={isEditing}
            error={errors.languages}
          />
        </div>
      )}

      {renderField(
        skills,
        setSkills,
        t("skills"),
        true,
        errors.skills,
        true,
      )}
      {renderField(extraInfo, setExtraInfo, t("extraInfo"), true)}

      {/* Контакты */}
      {(phones.some((phone) => phone?.trim()) ||
        email?.trim() ||
        links.some((link) => link?.trim()) ||
        Object.values(socialMedia).some((value) => value?.trim()) ||
        isEditing) && (
        <h1 className="text-xl font-bold uppercase text-[#006674] mb-2">
          {t("contacts")}
        </h1>
      )}

      {/* 🔹 Контакты: телефоны */}
      <PhoneNumbersField
        phones={phones}
        setPhones={setPhones}
        isEditing={isEditing}
        error={errors.phones}
      />

      {/* 🔹 Email */}
      <EmailField
        email={email}
        setEmail={setEmail}
        isEditing={isEditing}
        error={errors.email}
      />

      {/* 🔹 Ссылки */}
      <LinksField links={links} setLinks={setLinks} isEditing={isEditing} />

      {/* 🔹 Социальные сети */}
      <SocialMediaField
        socialMedia={socialMedia}
        setSocialMedia={setSocialMedia}
        isEditing={isEditing}
      />

      {/* 🔹 VisibilityToggle */}
      {isEditing && (
        <div className="w-full bg-[#006674] p-3 rounded-2xl text-white mb-8">
          <VisibilityToggle
            visibility={visibility}
            setVisibility={setVisibility}
          />
          <p className="font-normal w-[800px] max-lg:w-full mt-2">
            {t("visibilitydesc")}
          </p>
        </div>
      )}

      <div className="flex gap-3">
        {isEditing ? (
          <button
            onClick={handleSaveAll}
            className="py-2 px-6 bg-[#006674] text-white rounded-lg hover:bg-[#004c55]"
          >
            {t("saveChanges")}
          </button>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="py-2 px-6 bg-[#006674] text-white rounded-lg hover:bg-[#004c55]"
          >
            {t("edit")}
          </button>
        )}
      </div>
    </div>
  </div>
</section>
  );
};

export default ViewCandidatePage;